/**
 * +detail: shared helpers for the detail-pass props (outdoor/props/*): colour baking with smooth or
 * flat normals, consistent displacement of coincident vertices, small primitives. Same contract as
 * outdoor/procs.ts: non-indexed BufferGeometry in yards (position, normal, color), origin at the
 * base centre, facing +z.
 */
import * as THREE from 'three';

export type RGB3 = [number, number, number];

const tc = new THREE.Color();
/** sRGB hex → linear RGB triple. */
export const rgb = (hex: number): RGB3 => {
  tc.setHex(hex);
  return [tc.r, tc.g, tc.b];
};
export const mix = (a: RGB3, b: RGB3, t: number): RGB3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
export const scale3 = (a: RGB3, k: number): RGB3 => [a[0] * k, a[1] * k, a[2] * k];
export const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

/** The baked sun direction (matches the sky's sun: high, from the south-west, behind the default camera). */
export const SUN = new THREE.Vector3(-0.45, 0.75, 0.5).normalize();

/**
 * Non-indexed copy with a per-vertex colour from `fn` (position + normal). `flat` recomputes face
 * normals (facets); otherwise the incoming smooth normals are kept (soft canopy blobs).
 */
export function tint(geo: THREE.BufferGeometry, fn: (x: number, y: number, z: number, nx: number, ny: number, nz: number) => RGB3, flat = false): THREE.BufferGeometry {
  if (!geo.attributes['normal']) geo.computeVertexNormals();
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (g !== geo) geo.dispose();
  g.deleteAttribute('uv');
  if (flat) g.computeVertexNormals();
  const p = g.attributes['position'] as THREE.BufferAttribute;
  const n = g.attributes['normal'] as THREE.BufferAttribute;
  const col = new Float32Array(p.count * 3);
  for (let i = 0; i < p.count; i++) {
    const c = fn(p.getX(i), p.getY(i), p.getZ(i), n.getX(i), n.getY(i), n.getZ(i));
    col[i * 3] = c[0];
    col[i * 3 + 1] = c[1];
    col[i * 3 + 2] = c[2];
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

/** Displace coincident vertices of a (non-indexed) geometry consistently. */
export function displaceShared(g: THREE.BufferGeometry, fn: (v: THREE.Vector3) => void): void {
  const p = g.attributes['position'] as THREE.BufferAttribute;
  const seen = new Map<string, THREE.Vector3>();
  const v = new THREE.Vector3();
  for (let i = 0; i < p.count; i++) {
    const key = `${p.getX(i).toFixed(3)},${p.getY(i).toFixed(3)},${p.getZ(i).toFixed(3)}`;
    let out = seen.get(key);
    if (!out) {
      v.set(p.getX(i), p.getY(i), p.getZ(i));
      fn(v);
      out = v.clone();
      seen.set(key, out);
    }
    p.setXYZ(i, out.x, out.y, out.z);
  }
}

export const box = (w: number, h: number, d: number): THREE.BufferGeometry => new THREE.BoxGeometry(w, h, d).translate(0, h / 2, 0);
export const cyl = (rt: number, rb: number, h: number, seg = 8, hs = 1): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, h, seg, hs).translate(0, h / 2, 0);

/** A branch: a tapered cylinder from `a` to `b` (local yards). */
export function limb(a: RGB3, b: RGB3, r0: number, r1: number, seg = 6): THREE.BufferGeometry {
  const va = new THREE.Vector3(...a);
  const vb = new THREE.Vector3(...b);
  const len = va.distanceTo(vb);
  const g = new THREE.CylinderGeometry(r1, r0, len, seg, 1, true).translate(0, len / 2, 0);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), vb.clone().sub(va).normalize());
  g.applyQuaternion(q).translate(a[0], a[1], a[2]);
  return g;
}

/** A soft lumpy blob (smooth normals) for canopies and bushes; `flatBottom` squashes the underside. */
export function puff(r: number, rnd: () => number, lump = 0.14, ws = 8, hs = 6, flatBottom = 0.55): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(r, ws, hs);
  const p = g.attributes['position'] as THREE.BufferAttribute;
  const ox = rnd() * 10;
  const oz = rnd() * 10;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i);
    const y = p.getY(i);
    const z = p.getZ(i);
    const n = Math.sin((x * 2.3) / r + ox) * Math.cos((z * 2.1) / r + oz) * 0.5 + Math.sin((y * 2.9) / r + ox * 0.7) * 0.5;
    const k = 1 + lump * n;
    let yy = y * k;
    if (yy < -r * flatBottom) yy = -r * flatBottom + (yy + r * flatBottom) * 0.2;
    p.setXYZ(i, x * k, yy, z * k);
  }
  g.computeVertexNormals();
  return g;
}
